import translate from '@translate';
import React, { FC, useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setLocationParams } from 'src/resources/location/reducer';
import { RootStoreType } from 'src/types/store';
import { checkFormValidity, getEditData } from 'src/utils/formFunctions';
import Loading from 'src/components/Loading/intex';
import { useCheckPermission } from 'src/utils/checkPermission';
import { useNavigate } from 'react-router-dom';
import TopBlock from './TopBlock';
import Tree, { ReceivedRowType } from 'src/components/Tree';
import { DisableItemSelectType, SelectedItemsType } from 'src/components/Tree/Select';
import { addMessage } from 'src/resources/alertMessages/reducer';
import ConfirmDialog, { ConfirmDialogStatePropsType } from 'src/components/ConfirmDialog';
import { UserType } from 'src/types/user';
import { getRolesListFetch } from 'src/resources/rolesList/reducer';
import { getPermissionsListFetch } from 'src/resources/permissionsList/reducer';
import FormBlock from 'src/components/FormBlock';
import Detail from './Detail';
import {
    clearUserPermissionsDetail,
    getUserPermissionsDetailFetch,
    updateUserPermissionsDetailFetch,
} from 'src/resources/userPermissionsDetail/reducer';
import { OnChangeType, OptionType } from 'src/components/Select';


export const PERMISSION_ID_KEY = 'PID';

const UserPermissionsDetailContainer: FC = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { idUser } = useParams();

    const formRef = useRef<HTMLFormElement>(null);

    const data = useSelector((state: RootStoreType) => state.userPermissionsDetail.data);
    const loading = useSelector((state: RootStoreType) => state.userPermissionsDetail.loading);
    const loadingPermissionsList = useSelector((state: RootStoreType) => state.permissionsList.loading);
    const permissionsList = useSelector((state: RootStoreType) => state.permissionsList.data);
    const loadingRolesList = useSelector((state: RootStoreType) => state.rolesList.loading);
    const rolesList = useSelector((state: RootStoreType) => state.rolesList.data);
    const userLoggedPermissions = useSelector((state: RootStoreType) => state.user.data?.permissions);

    // const [selectedPermissions, setSelectedPermissions] = useState<SelectedItemsType>({});
    const [confirmParams, setConfirmParams] = useState<ConfirmDialogStatePropsType | null>(null);
    const [selectedRoles, setSelectedRoles] = useState<number[]>([]);
    const [editRolesPermisisons, setEditRolesPermisisons] = useState<SelectedItemsType>({});
    const [editPermisisons, setEditPermisisons] = useState<SelectedItemsType>({});
    const [allPermisisons, setAllPermisisons] = useState<SelectedItemsType>({});
    const [rolesOptions, setRolesOptions] = useState<OptionType[]>([]);
    const [disableEditPermissions, setDisableEditPermissions] = useState<SelectedItemsType>({});
    const [allDisablededitPermisisons, setAllDisablededitPermisisons] = useState<SelectedItemsType>({});

    const permissionUpdate = useCheckPermission('USER.PERMISSION.UPDATE');

    const backUrl = '/users/';

    useEffect(() => {
        if (idUser) {
            dispatch(setLocationParams({ name: translate('userPermissionsDetail.title', { idUser }), backUrl }));
        }
    }, [idUser, backUrl]);

    useEffect(() => {
        if (idUser) {
            dispatch(getUserPermissionsDetailFetch(idUser));
        }
        return () => {
            dispatch(clearUserPermissionsDetail());
        };
    }, [idUser]);

    // set permissions
    useEffect(() => {
        if (data) {
            setSelectedRoles(data.roles?.map((role) => role.ID_ROLE || 0) || []);

            const newEditPermisisons: SelectedItemsType = {};
            if (data.permissions) {
                data.permissions.forEach((permission) => {
                    if (permission[PERMISSION_ID_KEY]) {
                        newEditPermisisons[permission[PERMISSION_ID_KEY]] = true;
                    }
                });
            }
            setEditPermisisons(newEditPermisisons);
        }
    }, [data]);

    // join permissions and role permissions
    useEffect(() => {
        if (editRolesPermisisons && editPermisisons) {
            setAllPermisisons({ ...editRolesPermisisons, ...editPermisisons });
        }
    }, [editRolesPermisisons, editPermisisons]);

    // set permissions from selected roles
    useEffect(() => {
        if (rolesList) {
            const newRolesPermissions: SelectedItemsType = {};
            rolesList.forEach((role) => {
                if (role.ID_ROLE) {
                    if (selectedRoles.includes(role.ID_ROLE)) {
                        role.permissions?.forEach((permission) => {
                            if (permission[PERMISSION_ID_KEY]) {
                                newRolesPermissions[permission[PERMISSION_ID_KEY]] = true;
                            }
                        });
                    }
                }
            });
            setEditRolesPermisisons(newRolesPermissions);
        }
    }, [rolesList, selectedRoles]);
    useEffect(() => {
        if (permissionsList && userLoggedPermissions) {
            const newDisableEditPermissions: SelectedItemsType = {};
            permissionsList?.filter((permission) => {
                if (permission?.PID && !userLoggedPermissions?.includes(permission.PID)) {
                    newDisableEditPermissions[permission?.PID] = true;
                }
            });
            setDisableEditPermissions(newDisableEditPermissions);
        }
    }, [permissionsList, userLoggedPermissions]);

    useEffect(() => {
        if (!permissionsList) {
            dispatch(getPermissionsListFetch());
        }
    }, [permissionsList]);

    // load roles and set roles options
    useEffect(() => {
        if (!rolesList) {
            dispatch(getRolesListFetch());
        } else {
            setRolesOptions(
                rolesList.map((role): OptionType => ({ value: role.ID_ROLE || 0, label: role.ROLE_NAME || '' }))
            );
        }
    }, [rolesList]);

    useEffect(() => {
        setAllDisablededitPermisisons({ ...editRolesPermisisons, ...disableEditPermissions });
    }, [editRolesPermisisons, disableEditPermissions]);

    const onChangeRoles: OnChangeType = (value) => {
        if (Array.isArray(value)) {
            setSelectedRoles(value.map((val) => Number(val)));
        }
    };

    const onChangePermissions = (newSelected: SelectedItemsType) => {
        const newEditPermisisons: SelectedItemsType = {};
        Object.keys(newSelected).forEach((key) => {
            if (!editRolesPermisisons[key]) {
                newEditPermisisons[key] = true;
            }
        });
        setEditPermisisons(newEditPermisisons);
    };

    const handleSave = () => {
        setConfirmParams({
            title: translate('userPermissionsDetail.save'),
            content: translate('userPermissionsDetail.saveConfirm'),
            action: () => {
                const editData = {
                    ...data,
                    roles: rolesList
                        ?.filter((role) => selectedRoles.includes(role.ID_ROLE || 0))
                        .map((role) => ({
                            ID_ROLE: role.ID_ROLE,
                            ROLE_NAME: role.ROLE_NAME,
                            ROLE_INFO: role.ROLE_INFO,
                        })),
                    permissions: permissionsList?.filter(
                        (permission) => permission && editPermisisons[permission[PERMISSION_ID_KEY] || '']
                    ),
                };
                dispatch(updateUserPermissionsDetailFetch({ idUser, data: editData }));
            },
        });
    };

    return (
        <>
            <TopBlock permissionUpdate={permissionUpdate} handleSave={handleSave} />
            {loading && <Loading />}
            {data && (
                <FormBlock>
                    <Detail
                        onChangeRoles={onChangeRoles}
                        data={data}
                        permissionUpdate={permissionUpdate}
                        rolesOptions={rolesOptions}
                        selectedRoles={selectedRoles}
                    />
                </FormBlock>
            )}

            {permissionsList && (
                <Tree
                    loading={loadingPermissionsList}
                    data={permissionsList}
                    treeKey={PERMISSION_ID_KEY}
                    defaultTitle={translate('userPermissionsDetail.titleTree')}
                    selected={allPermisisons}
                    setSelected={onChangePermissions}
                    disabledSelect={!permissionUpdate}
                    disabledItemsSelect={allDisablededitPermisisons}
                />
            )}
            {confirmParams && (
                <ConfirmDialog
                    title={confirmParams.title}
                    content={confirmParams.content}
                    action={confirmParams.action}
                    onClose={(): any => setConfirmParams(null)}
                />
            )}
        </>
    );
};

export default UserPermissionsDetailContainer;
